import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { actions } from "./slice";
import { API_URL } from "utils/constants";
import { getDefaultHeaders } from "utils/helpers";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";
import {
  selectAdminPriceForm,
  selectAdminPricePage,
  selectAdminPricePerPage,
  selectSelectedId,
} from "./selector";
import { AdminPriceListResponse, AdminTableFormState } from "../types";

let isFetchingList = false;

function* handleGetPriceListSaga() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectAdminPricePage);
    const perPage: number = yield select(selectAdminPricePerPage);

    const response: AdminPriceListResponse = yield call(
      request,
      `${API_URL}/price-table?page=${page}&perPage=${perPage}`,
      { method: "GET", headers: getDefaultHeaders() }
    );

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(actions.getPriceListSuccess(payload));
    } else {
      yield put(actions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const page: number = yield select(selectAdminPricePage);
    if (page === 1) {
      yield put(actions.getPriceListFailed((error as Error).message));
    } else {
      yield put(actions.getNextPageItemsFailure((error as Error).message));
    }
  } finally {
    isFetchingList = false;
  }
}

function* createPriceListSaga() {
  try {
    const form: AdminTableFormState = yield select(selectAdminPriceForm);

    const options = {
      method: "POST",
      headers: getDefaultHeaders(),
      body: JSON.stringify({
        itemId: Number(form.itemId),
        unit: form.unit.trim(),
        basePrice: form.basePrice.trim(),
        lastUpdatedAt: form.lastUpdatedAt,
        toDate: form.toDate,
        priceListType: form.priceListType,
        vendorId: Number(form.vendorId),
        vendorName: form.vendorName,
      }),
    };
    const response = yield call(request, `${API_URL}/price-table`, options);
    yield put(actions.createPriceListSuccess(response.data));
  } catch (error) {
    yield put(actions.createPriceListFailed());
  }
}

function* getPriceDetailsSaga() {
  try {
    const id: number = yield select(selectSelectedId);
    const options = { method: "GET", headers: getDefaultHeaders() };
    const response = yield call(request, `${API_URL}/price-table/${id}`, options);
    yield put(actions.getPriceDetailsSuccess(response.data));
  } catch (error) {
    yield put(actions.getPriceDetailsFailure());
  }
}

function* updatePriceSaga(action: PayloadAction<{ id: number; form: AdminTableFormState }>) {
  try {
    const id: number = yield select(selectSelectedId);
    const options = {
      method: "PATCH",
      headers: getDefaultHeaders(),
      body: JSON.stringify(action.payload.form),
    };
    const response = yield call(request, `${API_URL}/price-table/${id}`, options);
    yield put(actions.updatePriceSuccess(response.data));
  } catch (error) {
    yield put(actions.updatePriceFailure());
  }
}

function* deletePriceSaga() {
  try {
    const id: number = yield select(selectSelectedId);
    const options = { method: "DELETE", headers: getDefaultHeaders() };
    yield call(request, `${API_URL}/price-table/${id}`, options);
    yield put(actions.deletePriceSuccess(id));
  } catch (error) {
    yield put(actions.deletePriceFailure());
  }
}


function* getDropDownsSaga() {
  try {
    const options = { method: "GET", headers: getDefaultHeaders() };
    const response = yield call(request, `${API_URL}/item?page=1&perPage=20`, options);
    const fixedResponse = response.data.map((item) => ({
      itemName: item.itemName,
      itemId: item.itemId,
    }));
    yield put(actions.getModalDropDownsSuccses(fixedResponse));
  } catch (error) {
    yield put(actions.getModalDropDownsFailer((error as Error).message));
  }
}

export function* AdminPrice() {
  yield takeLeading(actions.getPriceList.type, handleGetPriceListSaga);
  yield takeLeading(actions.getNextPageItems.type, handleGetPriceListSaga);
  yield takeLatest(actions.craeteForm.type, createPriceListSaga);
  yield takeLatest(actions.getPriceDetails.type, getPriceDetailsSaga);
  yield takeLatest(actions.updatePrice.type, updatePriceSaga);
  yield takeLatest(actions.deletePrice.type, deletePriceSaga);
  yield takeLatest(actions.getModalDropDowns.type, getDropDownsSaga);

}